import { useState } from "react";
import { auth } from "../firebase";
import { authFetch } from "../utils/authFetch";

const MOOD_COLORS = {
  happy     : "#4caf50", grateful: "#66bb6a", hopeful  : "#26a69a",
  calm      : "#42a5f5", neutral : "#78909c", tired    : "#8d6e63",
  sad       : "#5c6bc0", anxious : "#ef5350", overwhelmed: "#e53935",
  angry     : "#d32f2f", crisis  : "#b71c1c",
};

const MOOD_EMOJIS = {
  happy: "😊", grateful: "🙏", hopeful: "🌱", calm: "😌",
  neutral: "😐", tired: "😴", sad: "😢", anxious: "😰",
  overwhelmed: "😩", angry: "😤", crisis: "💙",
};

export default function QuickNote({ onNoteAdded }) {
  const [text, setText]         = useState("");
  const [insight, setInsight]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [limitReached, setLimitReached] = useState(false);

  const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
  const MAX_WORDS = 100;

  const handleSubmit = async () => {
    if (!text.trim() || loading) return;

    if (!auth.currentUser) {
      setError("Please log in to save your note.");
      return;
    }

    setLoading(true);
    setError("");
    setInsight(null);

    try {
      const res  = await authFetch('/api/quick-note/analyze', {
        method: 'POST',
        body  : JSON.stringify({ text }),
      });
      const data = await res.json();

      if (res.status === 429) {
        setLimitReached(true);
        setError(data.error || "Monthly limit reached.");
        return;
      }

      if (!res.ok) {
        setError(data.error || "Something went wrong. Please try again.");
        return;
      }

      setInsight(data);
      setText("");
      if (onNoteAdded) onNoteAdded(data);

    } catch {
      setError("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card quick-note-card">
      <h3>⚡ Quick Note</h3>
      <p style={{ color: "var(--text-secondary-color)", fontSize: "0.9rem", marginBottom: "16px" }}>
        How are you feeling right now? Just a few words is enough.
      </p>

      {/* Input */}
      <div style={{ position: "relative" }}>
        <textarea
          value={text}
          onChange={(e) => {
            const words = e.target.value.trim().split(/\s+/).filter(Boolean).length;
            if (words <= MAX_WORDS || e.target.value.length < text.length) {
              setText(e.target.value);
            }
          }}
          placeholder="I'm feeling a little overwhelmed today..."
          rows={3}
          style={{
            width        : "100%",
            padding      : "12px 16px",
            borderRadius : "12px",
            border       : "1.5px solid var(--border-color)",
            background   : "var(--input-bg)",
            color        : "var(--text-color)",
            fontSize     : "0.95rem",
            resize       : "none",
            boxSizing    : "border-box",
            lineHeight   : "1.6",
          }}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSubmit())}
        />
        <span style={{
          position : "absolute", bottom: "8px", right: "12px",
          fontSize : "0.75rem",
          color    : wordCount >= MAX_WORDS ? "#ef5350" : "var(--text-muted)",
        }}>
          {wordCount}/{MAX_WORDS}
        </span>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={!text.trim() || loading || limitReached}
        style={{
          marginTop    : "12px",
          padding      : "10px 24px",
          borderRadius : "10px",
          background   : loading ? "var(--border-color)" : "var(--primary-color)",
          color        : "#fff",
          border       : "none",
          cursor       : loading || !text.trim() ? "not-allowed" : "pointer",
          fontSize     : "0.95rem",
          fontWeight   : "600",
          width        : "100%",
          transition   : "opacity 0.2s",
        }}
      >
        {loading ? "Reflecting... 💭" : "Share this moment"}
      </button>

      {/* Error / Limit Reached */}
      {error && (
        <div style={{
          marginTop   : "12px", padding: "12px 16px",
          borderRadius: "10px",
          background  : limitReached ? "var(--warning-bg, #fff3e0)" : "var(--error-bg, #fef2f2)",
          color       : limitReached ? "#e65100" : "#c62828",
          fontSize    : "0.9rem",
        }}>
          {error}
          {limitReached && (
            <a href="/pricing" style={{ display: "block", marginTop: "8px", color: "var(--primary-color)", fontWeight: "600" }}>
              Upgrade to Plus →
            </a>
          )}
        </div>
      )}

      {/* Insight Response */}
      {insight && (
        <div style={{
          marginTop   : "16px",
          padding     : "16px",
          borderRadius: "12px",
          background  : insight.isCrisis ? "#e8f4fd" : "var(--card-bg-secondary, #f8f9fa)",
          borderLeft  : `4px solid ${MOOD_COLORS[insight.mood] || MOOD_COLORS.neutral}`,
          animation   : "fadeIn 0.4s ease",
        }}>
          {/* Mood Badge */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <span style={{
              padding     : "4px 12px", borderRadius: "20px",
              background  : MOOD_COLORS[insight.mood] || MOOD_COLORS.neutral,
              color       : "#fff", fontSize: "0.8rem", fontWeight: "600",
            }}>
              {MOOD_EMOJIS[insight.mood] || "💙"} {insight.mood}
            </span>
          </div>

          {/* Insight Text */}
          <p style={{
            margin: 0, fontSize: "0.95rem",
            color : "var(--text-color)", lineHeight: "1.7",
          }}>
            {insight.insight}
          </p>

          {/* Crisis Actions */}
          {insight.isCrisis && (
            <div style={{ marginTop: "12px" }}>
              <a href="tel:9152987821" style={{
                display: "inline-block", padding: "8px 16px",
                background: "#1976d2", color: "#fff",
                borderRadius: "8px", textDecoration: "none",
                fontSize: "0.9rem", fontWeight: "600",
              }}>
                📞 Call iCall Now — 9152987821
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}